import { useQuery } from '@apollo/client/react';
import dynamic from 'next/dynamic';
import React from 'react';

import { JobBoardFiltersInput } from '@pflegia/data-access';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { GEHALT } from '@pflegia/ui/pflegia/constants/routes';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';
import { getCityFallback } from '@pflegia/util/functions/job-position/helpers';

import { Skeleton, SkeletonRow } from '@shared/components/skeleton';
import { GET_JOB_POSITIONS_SALARY_ESTIMATIONS } from '@shared/graphql/operations/job-positions-salary-estimations';

type WrappedSalaryEstimationSectionProps = Partial<Pick<JobBoardFiltersInput, 'jobTitleIds' | 'locations'>> & {
  jobTitle?: string;
};

const LoadingSkeleton = () => (
  <Skeleton className='h-80 rounded-4xl bg-gray-200 md:h-40'>
    <SkeletonRow />
  </Skeleton>
);
const SalaryEstimationSection = dynamic(() => import('@pflegia/ui/shared/salary-estimation'), { loading: () => <LoadingSkeleton /> });

export const WrappedSalaryEstimationSection: React.FC<WrappedSalaryEstimationSectionProps> = ({ jobTitleIds, locations, jobTitle }) => {
  const { isPraxia } = useFlavorFromRoute();
  const skipSalaryEstimation = jobTitleIds?.length !== 1 || locations?.length !== 1;
  const location = getCityFallback(locations?.[0]);
  const { data: salaryEstimationData, loading: salaryEstimationDataLoading } = useQuery(GET_JOB_POSITIONS_SALARY_ESTIMATIONS, {
    skip: skipSalaryEstimation,
    variables: {
      filters: {
        jobTitleIds,
        locations,
      },
    },
  });

  if (skipSalaryEstimation || !salaryEstimationData?.data || !Object.values(salaryEstimationData?.data).every(Boolean)) return false;
  if (salaryEstimationDataLoading) return <LoadingSkeleton />;
  return (
    <SalaryEstimationSection
      {...salaryEstimationData.data}
      ctaHref={
        !isPraxia && jobTitle && location ? `${GEHALT}/${normalizeJobsParams(jobTitle)}/${normalizeJobsParams(location)}` : undefined
      }
      location={location}
      jobTitle={jobTitle}
    />
  );
};
